import { useEffect } from "react";
import { restaurantOrderApi } from "../api/restaurantOrderApi";
import { checkRestaurantPrinter, mapRestaurantPrintJob, submitRestaurantDocument } from "../features/restaurant/printer/restaurantPrinterAdapter";

const STORAGE_KEY = "bms.restaurantPrintStation.v1";
const BACKOFF_SECONDS = [1, 2, 4, 8, 16, 30];
const NO_JOB_DELAY_MS = 3000;
const BRIDGE_CHECK_INTERVAL_MS = 15000;

function readJournal() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch { return {}; }
}

function writeRecord(record) {
  try {
    const journal = readJournal();
    journal[String(record.jobId)] = record;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(journal));
  } catch (error) {
    throw new Error(`Cannot persist print recovery state: ${error?.message || "storage unavailable"}`);
  }
}

function removeRecord(jobId) {
  try {
    const journal = readJournal();
    delete journal[String(jobId)];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(journal));
  } catch { /* Keep the prior durable marker if browser storage is unavailable. */ }
}

function getPendingAck() {
  return Object.values(readJournal()).find((record) =>
    record?.state === "SUBMITTED" || record?.state === "ACK_PENDING" || record?.state === "FAILURE_ACK_PENDING",
  );
}

function delay(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve(false);
    const timer = setTimeout(() => resolve(true), ms);
    signal?.addEventListener("abort", () => { clearTimeout(timer); resolve(false); }, { once: true });
  });
}

export function useRestaurantPrintStation(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let backendBackoffIndex = 0;
    let bridgeBackoffIndex = 0;
    let bridgeAvailable = false;
    let nextBridgeCheckAt = 0;
    let controller = null;
    let uncertainStop = false;

    // An interrupted attempt could have reached the bridge. It is unsafe to resend it.
    const initial = readJournal();
    Object.values(initial).forEach((record) => {
      if (record?.state === "ATTEMPTING") {
        record.state = "UNCERTAIN";
        record.diagnostic = "Browser restarted before print submission was confirmed.";
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(initial)); } catch { /* Preserve best effort. */ }
      }
      if (record?.state === "UNCERTAIN") uncertainStop = true;
    });

    const nextBackoff = (domain) => {
      const index = domain === "bridge" ? bridgeBackoffIndex : backendBackoffIndex;
      const seconds = BACKOFF_SECONDS[Math.min(index, BACKOFF_SECONDS.length - 1)];
      if (domain === "bridge") bridgeBackoffIndex = Math.min(index + 1, BACKOFF_SECONDS.length - 1);
      else backendBackoffIndex = Math.min(index + 1, BACKOFF_SECONDS.length - 1);
      return seconds * 1000;
    };

    const ack = async (record, claimToken = record.claimToken) => {
      const current = { ...record, claimToken, state: record.state === "FAILURE_ACK_PENDING" ? "FAILURE_ACK_PENDING" : "ACK_PENDING" };
      writeRecord(current);
      try {
        if (current.state === "FAILURE_ACK_PENDING") {
          await restaurantOrderApi.markPrintJobFailed(current.jobId, claimToken, current.error, { signal: controller.signal });
        } else {
          await restaurantOrderApi.markPrintJobPrinted(current.jobId, claimToken, { signal: controller.signal });
        }
        removeRecord(current.jobId);
        backendBackoffIndex = 0;
        return "ACKNOWLEDGED";
      } catch (error) {
        if (error?.response?.status === 409) return "CLAIM_EXPIRED";
        return "RETRY_ACK";
      }
    };

    const loop = async () => {
      while (active && !uncertainStop) {
        controller = new AbortController();
        let expiredAckJobId = null;
        let errorDomain = "bridge";
        const pending = getPendingAck();
        if (pending) {
          const ackResult = await ack(pending);
          if (!active) break;
          if (ackResult === "RETRY_ACK") {
            if (!await delay(nextBackoff("backend"), controller.signal)) break;
            continue;
          }
          if (ackResult === "CLAIM_EXPIRED") {
            expiredAckJobId = pending.jobId;
            if (!await delay(nextBackoff("backend"), controller.signal)) break;
          }
        }

        try {
          if (!bridgeAvailable || Date.now() >= nextBridgeCheckAt) {
            bridgeAvailable = await checkRestaurantPrinter(controller.signal);
            nextBridgeCheckAt = Date.now() + BRIDGE_CHECK_INTERVAL_MS;
            if (!active) break;
            if (!bridgeAvailable) {
              if (!await delay(nextBackoff("bridge"), controller.signal)) break;
              continue;
            }
            bridgeBackoffIndex = 0;
          }

          errorDomain = "backend";
          const job = await restaurantOrderApi.claimNextPrintJob({ signal: controller.signal });
          if (!active) break;
          backendBackoffIndex = 0;
          if (!job) {
            if (!await delay(NO_JOB_DELAY_MS, controller.signal)) break;
            continue;
          }

          // Queue advancement proves the older acknowledgement was accepted; clear its journal marker.
          if (expiredAckJobId != null && String(job.jobId) !== String(expiredAckJobId)) {
            removeRecord(expiredAckJobId);
          }

          const oldRecord = readJournal()[String(job.jobId)];
          if (oldRecord?.state === "UNCERTAIN") {
            uncertainStop = true;
            break;
          }
          if (["SUBMITTED", "ACK_PENDING", "FAILURE_ACK_PENDING"].includes(oldRecord?.state)) {
            const result = await ack(oldRecord, job.claimToken);
            if (result === "RETRY_ACK" && active) await delay(nextBackoff("backend"), controller.signal);
            continue;
          }

          const mapped = mapRestaurantPrintJob(job);
          if (mapped.invalid) {
            const failed = {
              jobId: job.jobId,
              claimToken: job.claimToken,
              state: "FAILURE_ACK_PENDING",
              error: mapped.invalid,
            };
            const result = await ack(failed);
            if (result === "RETRY_ACK" && active) await delay(nextBackoff("backend"), controller.signal);
            continue;
          }

          // Journal before the bridge call: after reload this is treated as uncertain, never resent.
          writeRecord({ jobId: job.jobId, claimToken: job.claimToken, state: "ATTEMPTING" });
          const bridgeResult = await submitRestaurantDocument(mapped.payload, controller.signal);
          if (bridgeResult.kind === "SUBMITTED") {
            const submitted = { jobId: job.jobId, claimToken: job.claimToken, state: "SUBMITTED" };
            writeRecord(submitted);
            const result = await ack(submitted);
            if (result === "RETRY_ACK" && active) await delay(nextBackoff("backend"), controller.signal);
            continue;
          }
          if (bridgeResult.kind === "FAILED") {
            const failed = {
              jobId: job.jobId,
              claimToken: job.claimToken,
              state: "FAILURE_ACK_PENDING",
              error: `${bridgeResult.code}: ${bridgeResult.message}`,
            };
            const result = await ack(failed);
            if (result === "RETRY_ACK" && active) await delay(nextBackoff("backend"), controller.signal);
            continue;
          }
          writeRecord({
            jobId: job.jobId,
            claimToken: job.claimToken,
            state: "UNCERTAIN",
            diagnostic: bridgeResult.diagnostic,
          });
          uncertainStop = true;
        } catch (error) {
          if (!active || error?.name === "CanceledError" || error?.name === "AbortError") break;
          if (!await delay(nextBackoff(errorDomain), controller.signal)) break;
        }
      }
    };

    loop();
    return () => {
      active = false;
      controller?.abort();
    };
  }, [enabled]);
}
